/* eslint-disable max-len */
/* eslint-disable react/display-name */
import React from 'react';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { WorkspaceData } from '../../api/ApiTypes';
import { Arr } from '../../CodesForFoundation';
import { AccountModel } from '../account/AccountModel';
import { AppEvents, AppEventStreams } from '../AppEvents';
import { AppLocation } from '../AppLocation';
import { AppNavigator, Navigator } from '../AppNavigator';
import { Crash } from '../Crash';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { BoardModel, FolderModel, ModelBroker, UserModel, WorkspaceModel } from '../dashboard/Models';
import { PassportModel } from '../PassportModel';
import { ProfileModel } from '../ProfileModel';
import { createScope } from '../Scope';

const inspectEntity = (entityId: string) => entityId.startsWith('WO') ? 'workspace' : 'folder';

const fetchWorkspace = async (api: ApiClient, entityId: string): Promise<WorkspaceData> => {
    if (inspectEntity(entityId) === 'folder') {
        const { workspaceId } = await api.exec(v2.folders.info(entityId));
        return fetchWorkspace(api, workspaceId);
    }
    else {
        return await api.exec(v2.workspaces.info(entityId));
    }
};

const pushBoard = (broker: ModelBroker, board: BoardModel, toId: string) => {
    const to = (broker.get(FolderModel, toId) ?? broker.get(WorkspaceModel, toId)) as any;
    if (to) to.mutate(x => void (x.boards.push(board)));
};

const popBoard = (broker: ModelBroker, board: BoardModel) => {
    broker.get(WorkspaceModel, board.workspaceId)?.mutate(x => Arr.remove(x.boards, board));
    broker.get(FolderModel, board.folderId)?.mutate(x => Arr.remove(x.boards, board));
};

const onBoardCreate = (broker: ModelBroker) => (e: AppEventStreams['board:create']) => {
    // If for some reason (race condition) we already have, just exit
    if (broker.has(BoardModel, e.board.id)) return;
    // Create the new model
    const board = broker.allocate(BoardModel, e.board);
    // Add to appropriate location
    pushBoard(broker, board, board.folderId ?? board.workspaceId);
};

const onBoardDelete = (broker: ModelBroker) => (e: AppEventStreams['board:delete']) => {
    const board = broker.get(BoardModel, e.boardId);
    if (board) popBoard(broker, board);
};

const onBoardPatch = (broker: ModelBroker) => (e: AppEventStreams['board:patch']) =>
    broker.get(BoardModel, e.board.id)?.mutate(e.board);

const onBoardMove = (broker: ModelBroker, workspace: WorkspaceModel) => (e: AppEventStreams['board:move']) => {
    // Find the board model
    const board = broker.get(BoardModel, e.boardId);
    if (!board) return;
    // Remove from workspace if applicable
    if (workspace.boards.includes(board)) {
        workspace.mutate(x => Arr.remove(x.boards, board));
    }
    // Remove from folder if applicable
    for (const folder of workspace.folders) {
        if (folder.boards.includes(board)) {
            folder.mutate(x => Arr.remove(x.boards, board));
        }
    }
    // Update model
    board.mutate({ workspaceId: e.workspaceId, folderId: e.folderId });
    // Add if applicable
    pushBoard(broker, board, board.folderId ?? board.workspaceId);
};

const onWorkspacePatch = (broker: ModelBroker) => (e: AppEventStreams['workspace:patch']) =>
    broker.get(WorkspaceModel, e.workspace.id)?.mutate(e.workspace);

const onWorkspaceMemberAdd = (broker: ModelBroker, me: ProfileModel) => (e: AppEventStreams['workspace:members:add']) => {
    broker.get(WorkspaceModel, e.workspace.id)?.mutate(x => {
        x.joined = x.joined || e.user.id === me.id;
        x.members.push(broker.allocate(UserModel, e.user));
    });
};

// eslint-disable-next-line max-len
const onWorkspaceMemberRemove = (broker: ModelBroker, me: ProfileModel) => (e: AppEventStreams['workspace:members:remove']) => {
    broker.get(WorkspaceModel, e.workspaceId)?.mutate(x => {
        x.joined = e.userId === me.id ? false : x.joined;
        Arr.removeWhere(x.members, u => u.id === e.userId);
    });
};

const onWorkspaceDelete = (broker: ModelBroker, location: AppLocation, navigator: Navigator) => (e: AppEventStreams['workspace:delete']) => {
    // Redirect navigator if we are in location of folder
    if (location.includes('workspace', e.workspaceId)) {
        navigator.go('/');
    }
};

const onFolderCreate = (broker: ModelBroker) => (e: AppEventStreams['folder:create']) => {
    const fd = broker.allocate(FolderModel, { ...e.folder, boards: [] });
    const ws = broker.get(WorkspaceModel, fd.workspaceId);
    ws?.mutate(x => x.folders.push(fd));
};

const onFolderPatch = (broker: ModelBroker) => (e: AppEventStreams['folder:patch']) => {
    const fd = broker.get(FolderModel, e.folder.id);
    if (!fd) return;
    fd.mutate(e.folder);
};

const onFolderDelete = (broker: ModelBroker, location: AppLocation, navigator: Navigator) => (e: AppEventStreams['folder:delete']) => {
    const fd = broker.get(FolderModel, e.folderId);
    if (!fd) return;
    const ws = broker.get(WorkspaceModel, fd.workspaceId);
    if (!ws) return;
    // Move boards to workspace and remove folder from workspace
    ws.mutate(s => {
        Arr.remove(s.folders, fd);
        s.boards.push(...fd.boards);
    });
    // Redirect navigator if we are in location of folder
    if (location.includes('folder', fd.id)) {
        navigator.go(`~/${ws.id}`);
    }
};

export const NullWorkspace = new WorkspaceModel({

});

export const WorkspaceCrash = React.memo<{ error: Error }>(({ error }) => {
    return <Flexer absolute inset flex>
        <Crash error={error} refreshOn403 />
    </Flexer>;
});

export const NewWorkspaceScope = createScope<{ entityId: string }>({
    traceAs: 'NewWorkspaceScope',
    shouldRefresh(p, c) {
        const m = c.get(WorkspaceModel);
        return m.id !== p.entityId && !m.findFolder(p.entityId);
    },
    async refresh(p, c) {

        const api = c.get(ApiClient);
        const evts = c.get(AppEvents);
        const broker = c.get(ModelBroker);
        const location = c.get(AppLocation);
        const navigator = c.get(AppNavigator);
        const me = c.get(ProfileModel);
        const account = c.get(AccountModel);
        const rootPassport = c.get(PassportModel);

        if (p.entityId === account.id) {
            c.bind(WorkspaceModel).toConstantValue(WorkspaceModel.createNull(account.id));
        }
        else {
            const workspaceData = await fetchWorkspace(api, p.entityId);
            const membersData = await api.exec(v2.workspaces.members.list(workspaceData.id));
            const permissionsData = await api.exec(v2.me.permissions(['workspace', workspaceData.id]));

            const workspace = broker.allocate(WorkspaceModel, workspaceData);
            workspace.mutate({
                joined: !!membersData.find(x => x.id === me.id),
                members: membersData.map(x => broker.allocate(UserModel, x)),
            });

            const workspacePassport = PassportModel.create(rootPassport, permissionsData);

            c.bind(PassportModel).toConstantValue(workspacePassport);
            c.bind(WorkspaceModel).toConstantValue(workspace);
        }

        return [
            // Mutate model on changes:
            evts.on('board:create', onBoardCreate(broker)),
            evts.on('board:delete', onBoardDelete(broker)),
            evts.on('board:patch', onBoardPatch(broker)),
            evts.on('board:move', onBoardMove(broker, c.get(WorkspaceModel))),
            evts.on('workspace:patch', onWorkspacePatch(broker)),
            evts.on('workspace:delete', onWorkspaceDelete(broker, location, navigator)),
            evts.on('workspace:members:add', onWorkspaceMemberAdd(broker, me)),
            evts.on('workspace:members:remove', onWorkspaceMemberRemove(broker, me)),
            evts.on('folder:create', onFolderCreate(broker)),
            evts.on('folder:patch', onFolderPatch(broker)),
            evts.on('folder:delete', onFolderDelete(broker, location, navigator)),
        ];
    }
});

